import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService.js';
import { setAuthToken as setApiClientToken, setUnauthorizedCallback } from '../api/apiClient.js';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState('');
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState('');

  const clearAuthData = useCallback(() => {
    localStorage.removeItem('ai_auth_token');
    localStorage.removeItem('ai_oauth_token');
    localStorage.removeItem('ai_refresh_token');
    localStorage.removeItem('ai_token_expiry');
    localStorage.removeItem('ai_user_profile');
    setAuthToken('');
    setUser(null);
  }, []);

  useEffect(() => {
    const handleAuthError = () => {
      clearAuthData();
      setError('Your session has expired. Please log in again.');
    };

    apiService.setAuthErrorCallback(handleAuthError);
    setUnauthorizedCallback(handleAuthError);
    return () => apiService.setAuthErrorCallback(null);
  }, [clearAuthData]);

  // Sync token to axios instance whenever it changes
  useEffect(() => {
    setApiClientToken(authToken);
  }, [authToken]);

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const isLocalJwtToken = (token) =>
    token &&
    token.startsWith('eyJ') &&
    !token.startsWith('ya29.') &&
    !token.startsWith('ya30.') &&
    !token.startsWith('ya31.');

  const validateToken = useCallback(
    async (token) => {
      try {
        setIsLoading(true);
        setError('');

        if (isLocalJwtToken(token)) {
          const profileResult = await apiService.localGetProfile(token);
          if (profileResult.ok && profileResult.data?.success) {
            setAuthToken(token);
            setUser(profileResult.data.profile);
            localStorage.setItem('ai_oauth_token', token);
            localStorage.setItem('ai_user_profile', JSON.stringify(profileResult.data.profile));
            return { success: true };
          } else {
            clearAuthData();
            setError(profileResult.data?.error || profileResult.error || 'Invalid or expired token');
            return { success: false };
          }
        }

        const profileResult = await apiService.getUserProfile(token);
        if (profileResult.ok && profileResult.data) {
          setAuthToken(token);
          setUser(profileResult.data.profile);
          localStorage.setItem('ai_oauth_token', token);
          localStorage.setItem('ai_user_profile', JSON.stringify(profileResult.data.profile));
          return { success: true };
        }

        const result = await apiService.validateToken(token);
        if (result.ok) {
          setAuthToken(token);
          localStorage.setItem('ai_auth_token', token);
          return { success: true };
        }

        clearAuthData();
        setError(result.error || 'Invalid or expired token');
        return { success: false };
      } catch {
        setError('Network error during authentication');
        return { success: false };
      } finally {
        setIsLoading(false);
      }
    },
    [clearAuthData],
  );

  useEffect(() => {
    const initializeAuth = async () => {
      const oauthToken = getCookie('ai_oauth_token');
      const refreshToken = getCookie('ai_refresh_token');
      const tokenExpiry = getCookie('ai_token_expiry');

      if (oauthToken) {
        document.cookie = 'ai_oauth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        localStorage.setItem('ai_oauth_token', oauthToken);
        if (refreshToken) {
          localStorage.setItem('ai_refresh_token', refreshToken);
          document.cookie = 'ai_refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
        if (tokenExpiry) {
          localStorage.setItem('ai_token_expiry', tokenExpiry);
          document.cookie = 'ai_token_expiry=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
        await validateToken(oauthToken);
      } else {
        const token = localStorage.getItem('ai_auth_token') || localStorage.getItem('ai_oauth_token');
        const userProfile = localStorage.getItem('ai_user_profile');
        if (token) {
          if (userProfile) {
            try {
              setUser(JSON.parse(userProfile));
            } catch {
              /* empty */
            }
          }
          await validateToken(token);
        }
      }

      setIsInitializing(false);
    };

    initializeAuth();
  }, [validateToken]);

  const login = useCallback(
    async (token) => {
      if (!token?.trim()) {
        setError('Please enter a valid token');
        return { success: false };
      }
      return validateToken(token);
    },
    [validateToken],
  );

  const logout = useCallback(() => {
    clearAuthData();
    setError('');
    return { success: true };
  }, [clearAuthData]);

  const clearError = useCallback(() => setError(''), []);

  return (
    <AuthContext.Provider
      value={{
        authToken,
        user,
        isLoading,
        isInitializing,
        error,
        isAuthenticated: !!authToken,
        login,
        logout,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
